import React, { useEffect, useMemo, useState } from "react";
import { getTitleForLevel } from "../utils/levelUtils";
import { playSystemSound } from "../audio/soundManager";

const RANK_CHANGE_CEREMONY_MS = 2000;
const LEVEL_ONLY_CEREMONY_MS = 1200;

const LevelUpModal = ({ show, onClose, report, loading }) => {
  const [ceremonyUnlocked, setCeremonyUnlocked] = useState(false);

  const lvl = Number(report?.level || 0);
  const title = report?.title || "Rank recalibration in progress";
  const previousLevel = Math.max(1, lvl - 1);
  const previousTitle = getTitleForLevel(previousLevel);
  const isRankChange = previousTitle !== title;

  useEffect(() => {
    if (!show) {
      setCeremonyUnlocked(false);
      return undefined;
    }

    setCeremonyUnlocked(false);
    const timeoutId = window.setTimeout(
      () => setCeremonyUnlocked(true),
      isRankChange ? RANK_CHANGE_CEREMONY_MS : LEVEL_ONLY_CEREMONY_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [isRankChange, show, title, lvl]);

  useEffect(() => {
    if (!show) return;
    playSystemSound(isRankChange ? "rankUp" : "levelUp");
  }, [isRankChange, show]);

  const acknowledgementDisabled = !ceremonyUnlocked || loading;

  const ceremonyClassName = useMemo(
    () =>
      [
        "level-up-cinematic",
        isRankChange
          ? "level-up-cinematic--rank-change"
          : "level-up-cinematic--level-only",
      ].join(" "),
    [isRankChange]
  );

  if (!show) return null;

  return (
    <div className={ceremonyClassName}>
      <div className="level-up-cinematic__backdrop" />

      {isRankChange && (
        <div className="level-up-cinematic__archived">
          <div className="level-up-cinematic__archived-label">
            [ RANK: {previousTitle} ] - ARCHIVED
          </div>
        </div>
      )}

      <div className="level-up-cinematic__core">
        <div className="level-up-cinematic__level">LEVEL {lvl || "..."}</div>
        <div className="level-up-cinematic__title">{title}</div>
        <div className="level-up-cinematic__confirmation">
          {isRankChange
            ? "RANK ADVANCEMENT CONFIRMED"
            : "LEVEL ADVANCEMENT CONFIRMED"}
        </div>
      </div>

      <div className="level-up-cinematic__report">
        {loading ? (
          <div className="level-up-cinematic__placeholder">
            [ SYSTEM COMPILING ASSESSMENT... ]
          </div>
        ) : (
          <>
            {report?.motivation && (
              <section className="level-up-cinematic__panel">
                <div className="level-up-cinematic__panel-label">
                  [SYSTEM NOTICE]
                </div>
                <div className="level-up-cinematic__panel-copy">
                  {report.motivation}
                </div>
              </section>
            )}

            {Array.isArray(report?.criticism) && report.criticism.length > 0 && (
              <section className="level-up-cinematic__panel">
                <div className="level-up-cinematic__panel-label">
                  [CONSTRUCTIVE FEEDBACK]
                </div>
                <ul className="level-up-cinematic__feedback-list">
                  {report.criticism.map((entry, index) => (
                    <li key={`${entry}-${index}`}>{entry}</li>
                  ))}
                </ul>
              </section>
            )}

            {report?.quest?.title && (
              <section className="levelup-directive-card levelup-directive-card--cinematic">
                <div className="levelup-directive-card__label">
                  [ SYSTEM DIRECTIVE - NEXT ASSIGNMENT ]
                </div>
                <div className="levelup-directive-card__title">
                  {report.quest.title}
                </div>
                {report.quest?.desc && (
                  <div className="levelup-directive-card__desc">
                    {report.quest.desc}
                  </div>
                )}
                {Number.isFinite(Number(report.quest?.xp)) && (
                  <div className="levelup-directive-card__xp">
                    +{Number(report.quest.xp)} XP on completion
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>

      <div className="level-up-cinematic__actions">
        <div className="level-up-cinematic__hold">
          {!ceremonyUnlocked
            ? "[CEREMONY IN PROGRESS] Hold position."
            : loading
              ? "[SYSTEM HOLD] Assessment still compiling."
              : "[SYSTEM CLEARANCE] Acknowledge the advancement."}
        </div>
        <button
          className="login-btn level-up-cinematic__acknowledge"
          type="button"
          onClick={onClose}
          disabled={acknowledgementDisabled}
        >
          [ ACKNOWLEDGE ]
        </button>
      </div>
    </div>
  );
};

export default LevelUpModal;
